// Admin → Settings: the server's settings form (registration, security, email, TURN, styling, features...).
import { Lock, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { refreshCustomCss, type CssArea } from '../../lib/customCss';
import type { LockoutPolicy, PasskeyPolicy, SupportPolicy, BootSettings, Parent, PlatformSettings } from '../../lib/types';
import { EmailUsage } from '../EmailUsage';
import { useParent } from '../ParentContext';
import { PaymentsSettings } from '../Billing';
import { BillingNotices } from './BillingNotices';
import { SignupCodes } from './SignupCodes';
import { TermsSettings } from './TermsSettings';
import { Collapsible, Fold } from '../../ui/Collapsible';
import { SupportSettings } from '../Support';
import { PasskeyPolicySettings } from '../Passkeys';
import { LockoutSettings, SigninSecurityReport } from '../SigninSecurity';
import { ThemeGrid } from '../../ui/ThemePicker';
import { paletteById } from '../../lib/palette';
import { CssViewer } from '../CssViewer';
import { CHILD_FEATURE_GROUPS, FAMILY_FEATURES } from '../ChildFeatures';
import { OLD_ANCHORS, SettingsSearch } from './SettingsSearch';
import { ExtensionPackage } from './ExtensionPackage';
import { ApiAccess } from './ApiAccess';

/** The School Resources themes (#819), in the apps' menu order. Must match server/src/agent-themes.ts. */
const AGENT_THEME_IDS = ['ocean-mist', 'ocean-depths', 'aurora', 'northern-lights', 'ember', 'cotton-candy', 'peach', 'mint', 'lavender'];

// --- Server settings ----------------------------------------------------------------------

/** Where custom CSS applies (#367); 'all' loads first and the others go on top of it. */
const CSS_AREAS: { id: CssArea; label: string }[] = [
  { id: 'all', label: 'Every page (including the default themes)' },
  { id: 'landing', label: 'Landing page' },
  { id: 'login', label: 'Sign-in pages (sign in, sign up, password, invites)' },
  { id: 'agent', label: 'Child’s computer (agent page)' },
  { id: 'student', label: 'Student portal' },
];
interface SettingsForm {
  publicUrl: string;
  registrationOpen: boolean;
  schoolReaders: boolean;
  connexusBookmark: boolean;
  studentPortalLinks: boolean;
  offTaskAlerts: boolean;
  schoolReadHours: number;
  customCss: Record<CssArea, string>;
  requireMfa: 'off' | 'admins' | 'everyone';
  lockout: LockoutPolicy;
  passkeys: PasskeyPolicy;
  defaultTheme: string;
  agentDefaultTheme: string;
  support: SupportPolicy;
  adminEmails: string;
  offlineGraceSeconds: number;
  retentionDays: number;
  signInHistoryDays: number;
  accountDeletionDays: number;
  agentUpdateHours: number;
  launcherCollapseSeconds: number;
  cameraBlur: { parent: boolean; child: boolean; byDefault: boolean };
  ghostMode: { allowed: boolean };
  agentHiding: { allowed: boolean; byDefault: boolean };
  portfolio: { enabled: boolean; studentUploads: boolean; maxMb: number; familyStorageMb: number; retentionYears: number; tests: boolean; evaluatorLinks: boolean; linkMaxDays: number; linksPerFamily: number; transcript: boolean; honorsBonus: number; apBonus: number; defaultCredits: number; fieldTrips: boolean; tripPhotos: number; tripMaxHours: number; documents: boolean; documentMaxMb: number; closeOut: boolean };
  weeklySummary: { enabled: boolean; day: number; hour: number; defaultOn: boolean };
  onboarding: { enabled: boolean; steps: string[] };
  webhookAllowPrivate: boolean;
  alsoForAll: boolean;
  defaultHiddenFeatures: string[];
  disabledFeatures: string[];
  stunUrls: string;
  turnUrls: string;
  turnUser: string;
  turnCred: string;
  turnCredClear: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecurity: 'tls' | 'starttls' | 'none';
  smtpUser: string;
  smtpPass: string;
  smtpPassClear: boolean;
  smtpFrom: string;
  ghRepo: string;
  ghToken: string;
  ghTokenClear: boolean;
  ghAppId: string;
  ghKey: string;
  ghKeyClear: boolean;
  extChromeId: string;
  extEdgeId: string;
  notifySignups: boolean;
  notifyFeedback: boolean;
  host: string;
  port: number;
  trustProxy: boolean;
}
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const hourLabel = (h: number) => new Date(2000, 0, 1, h).toLocaleTimeString(undefined, { hour: 'numeric' });
const lines = (s: string) => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
function toForm(s: PlatformSettings): SettingsForm {
  return {
    publicUrl: s.publicUrl,
    registrationOpen: s.registrationOpen,
    schoolReaders: s.schoolReaders ?? true,
    connexusBookmark: s.connexusBookmark ?? true,
    studentPortalLinks: s.studentPortalLinks ?? true,
    offTaskAlerts: s.offTaskAlerts ?? false,
    schoolReadHours: s.schoolReadHours ?? 4,
    customCss: { all: '', landing: '', login: '', agent: '', student: '', ...s.customCss },
    requireMfa: s.requireMfa,
    lockout: { ...s.lockout },
    passkeys: { ...s.passkeys },
    support: { ...s.support, types: [...s.support.types], labels: { ...s.support.labels } },
    defaultTheme: s.defaultTheme,
    agentDefaultTheme: s.agentDefaultTheme ?? 'ocean-mist',
    adminEmails: s.adminEmails.join('\n'),
    offlineGraceSeconds: s.offlineGraceSeconds,
    retentionDays: s.retentionDays,
    signInHistoryDays: s.signInHistoryDays ?? 365,
    accountDeletionDays: s.accountDeletionDays ?? 14,
    agentUpdateHours: s.agentUpdateHours ?? 6,
    launcherCollapseSeconds: s.launcherCollapseSeconds ?? 5,
    cameraBlur: { parent: true, child: true, byDefault: false, ...s.cameraBlur },
    ghostMode: { allowed: true, ...s.ghostMode },
    agentHiding: { allowed: true, byDefault: false, ...s.agentHiding },
    portfolio: { enabled: true, studentUploads: true, maxMb: 10, familyStorageMb: 500, retentionYears: 0, tests: true, evaluatorLinks: true, linkMaxDays: 90, linksPerFamily: 10, transcript: true, honorsBonus: 0.5, apBonus: 1, defaultCredits: 1, fieldTrips: true, tripPhotos: 6, tripMaxHours: 8, documents: true, documentMaxMb: 25, closeOut: true, ...s.portfolio },
    weeklySummary: s.weeklySummary ?? { enabled: true, day: 0, hour: 18, defaultOn: true },
    onboarding: s.onboarding ?? { enabled: true, steps: ['child', 'pair', 'hours', 'assignment'] },
    webhookAllowPrivate: s.webhookAllowPrivate,
    alsoForAll: s.alsoForAll ?? true,
    defaultHiddenFeatures: s.defaultHiddenFeatures ?? [],
    disabledFeatures: s.disabledFeatures ?? [],
    stunUrls: s.stunUrls.join('\n'),
    turnUrls: s.turn.urls.join('\n'),
    turnUser: s.turn.username,
    turnCred: '',
    turnCredClear: false,
    smtpHost: s.smtp.host,
    smtpPort: s.smtp.port,
    smtpSecurity: s.smtp.security,
    smtpUser: s.smtp.username,
    smtpPass: '',
    smtpPassClear: false,
    smtpFrom: s.smtp.from,
    ghRepo: s.github.repo,
    ghToken: '',
    ghTokenClear: false,
    ghAppId: s.github.appId ?? '',
    ghKey: '',
    ghKeyClear: false,
    extChromeId: s.extensionStore.chromeId,
    extEdgeId: s.extensionStore.edgeId,
    notifySignups: s.platformNotifications.signups,
    notifyFeedback: s.platformNotifications.feedback,
    host: s.boot.host,
    port: s.boot.port,
    trustProxy: s.boot.trustProxy,
  };
}
/** A secret field: blank keeps what's saved, "Remove" clears it. */
const secretValue = (value: string, clear: boolean) => (clear ? null : value.trim() || undefined);
export function ServerSettings() {
  const { parent, toast, setParent } = useParent();
  const [saved, setSaved] = useState<PlatformSettings | null>(null);
  const [form, setForm] = useState<SettingsForm | null>(null);
  const [busy, setBusy] = useState(false);
  // Search (#630): the query, and an option to open and flash after a result is picked.
  const [query, setQuery] = useState('');
  const [flash, setFlash] = useState<{ target: string; label: string } | null>(null);
  const jump = (target: string, label: string) => {
    history.replaceState(null, '', `#${target}`);
    setQuery('');
    setFlash({ target, label });
  };
  // Links to sections from before the regrouping (#630) go to where they are now.
  useEffect(() => {
    const to = OLD_ANCHORS[location.hash.slice(1)];
    if (to) history.replaceState(null, '', `#${to}`);
  }, []);
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => {
      const root = document.getElementById(flash.target);
      if (!root) return;
      // The option itself if it can be found by its name, else the section or fold.
      const key = flash.label.split(/[:(]/)[0]!.trim().toLowerCase();
      const el = [...root.querySelectorAll<HTMLElement>('label, .fold-head, h3')].find((x) => x.textContent?.toLowerCase().includes(key)) ?? root;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('setting-flash');
      setTimeout(() => el.classList.remove('setting-flash'), 2200);
      setFlash(null);
    }, 150);
    return () => clearTimeout(t);
  }, [flash]);

  const apply = useCallback((s: PlatformSettings) => {
    setSaved(s);
    setForm(toForm(s));
  }, []);

  useEffect(() => {
    api.get<PlatformSettings>('/api/admin/settings').then(apply).catch((e) => toast({ text: e.message, tone: 'alert' }));
  }, [apply, toast]);

  if (!saved || !form) return <p className="muted">Loading…</p>;

  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(saved));
  const set = <K extends keyof SettingsForm>(k: K, v: SettingsForm[K]) => setForm((f) => (f ? { ...f, [k]: v } : f));

  // Fold summaries (#601).
  const adminCount = new Set([...saved.envAdminEmails, ...lines(form.adminEmails)].map((e) => e.toLowerCase())).size;
  const stunCount = lines(form.stunUrls).length;
  const blur = form.cameraBlur;
  const blurWho = blur.parent && blur.child ? 'Parents and children' : blur.parent ? 'Parents only' : blur.child ? 'Children only' : '';
  const blurSummary = blurWho ? `${blurWho} · ${blur.byDefault ? 'on' : 'off'} by default` : 'Off';
  const ws = form.weeklySummary;
  const cssSet = Object.values(form.customCss).filter((v) => v.trim()).length;
  const cssSummary = cssSet ? `${cssSet} area${cssSet === 1 ? '' : 's'} styled` : 'Standard look';
  const weeklySummary = ws.enabled ? `${WEEKDAYS[ws.day]}s at ${hourLabel(ws.hour)} · ${ws.defaultOn ? 'on' : 'off'} for new parents` : 'Off';

  async function save() {
    if (!form) return;
    setBusy(true);
    try {
      const next = await api.put<PlatformSettings>('/api/admin/settings', {
        publicUrl: form.publicUrl,
        registrationOpen: form.registrationOpen,
        schoolReaders: form.schoolReaders,
        connexusBookmark: form.connexusBookmark,
        studentPortalLinks: form.studentPortalLinks,
        offTaskAlerts: form.offTaskAlerts,
        schoolReadHours: Number(form.schoolReadHours),
        customCss: form.customCss,
        requireMfa: form.requireMfa,
        lockout: form.lockout,
        passkeys: form.passkeys,
        support: form.support,
        defaultTheme: form.defaultTheme,
        agentDefaultTheme: form.agentDefaultTheme,
        adminEmails: lines(form.adminEmails),
        offlineGraceSeconds: Number(form.offlineGraceSeconds),
        retentionDays: Number(form.retentionDays),
        signInHistoryDays: Number(form.signInHistoryDays),
        accountDeletionDays: Number(form.accountDeletionDays),
        agentUpdateHours: Number(form.agentUpdateHours),
        launcherCollapseSeconds: Number(form.launcherCollapseSeconds),
        cameraBlur: form.cameraBlur,
        ghostMode: form.ghostMode,
        agentHiding: form.agentHiding,
        portfolio: { ...form.portfolio, maxMb: Number(form.portfolio.maxMb), familyStorageMb: Number(form.portfolio.familyStorageMb), retentionYears: Number(form.portfolio.retentionYears), linkMaxDays: Number(form.portfolio.linkMaxDays), linksPerFamily: Number(form.portfolio.linksPerFamily), honorsBonus: Number(form.portfolio.honorsBonus), apBonus: Number(form.portfolio.apBonus), defaultCredits: Number(form.portfolio.defaultCredits) },
        weeklySummary: form.weeklySummary,
        onboarding: form.onboarding,
        webhookAllowPrivate: form.webhookAllowPrivate,
        alsoForAll: form.alsoForAll,
        defaultHiddenFeatures: form.defaultHiddenFeatures,
        disabledFeatures: form.disabledFeatures,
        stunUrls: lines(form.stunUrls),
        turn: { urls: lines(form.turnUrls), username: form.turnUser, credential: secretValue(form.turnCred, form.turnCredClear) },
        smtp: {
          host: form.smtpHost,
          port: Number(form.smtpPort),
          security: form.smtpSecurity,
          username: form.smtpUser,
          password: secretValue(form.smtpPass, form.smtpPassClear),
          from: form.smtpFrom,
        },
        github: { repo: form.ghRepo, token: secretValue(form.ghToken, form.ghTokenClear), appId: form.ghAppId, privateKey: secretValue(form.ghKey, form.ghKeyClear) },
        extensionStore: { chromeId: form.extChromeId.trim(), edgeId: form.extEdgeId.trim() },
        platformNotifications: { signups: form.notifySignups, feedback: form.notifyFeedback },
        host: form.host,
        port: Number(form.port),
        trustProxy: form.trustProxy,
      });
      apply(next);
      // Your own account may follow the default theme (#360): pick up a change right away.
      if (next.defaultTheme !== saved?.defaultTheme) api.get<Parent>('/api/me').then(setParent).catch(() => undefined);
      refreshCustomCss(); // show new custom styles on this page right away (#367)
      toast({
        text: next.restartNeeded ? 'Saved. Some changes apply after a restart.' : 'Server settings saved. They apply right away.',
        tone: 'good',
      });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setBusy(false);
    }
  }

  async function testWeekly() {
    try {
      const r = await api.post<{ to: string }>('/api/admin/weekly-summary/test');
      toast({ text: `Your weekly summary was sent to ${r.to}.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  async function testEmail() {
    try {
      const r = await api.post<{ to: string }>('/api/admin/email/test');
      toast({ text: `Test email sent to ${r.to}.`, tone: 'good' });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  async function testGithub() {
    try {
      const r = await api.post<{ fullName: string; private: boolean; hasIssues: boolean; as?: 'app' | 'token' }>('/api/admin/github/test');
      toast({
        text: r.hasIssues ? `Connected to ${r.fullName}${r.private ? ' (private)' : ''}${r.as === 'app' ? ' as the GitHub App' : ''}.` : `${r.fullName} has Issues turned off.`,
        tone: r.hasIssues ? 'good' : 'alert',
      });
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    }
  }

  return (
    <div className="stack narrow-col">
      {saved.restartNeeded && <RestartNotice saved={saved} />}

      <SettingsSearch query={query} onQuery={setQuery} onJump={jump} />
      {!query && (
        <>
      <h3 className="settings-group">Site</h3>
      <Collapsible id="s-site" title="Site" anchors={['site-theme', 'site-agent-theme', 'site-agent-hiding', 'site-css']}>
        <label>
          Public address <span className="muted small">(how parents reach ClassGuardian, e.g. https://classguardian.example.com)</span>
          <input value={form.publicUrl} onChange={(e) => set('publicUrl', e.target.value)} placeholder="Leave empty to use the address in each request" />
          <span className="muted small">Used for links in emails, like invites and password resets. Set it when ClassGuardian is behind a reverse proxy.</span>
        </label>
        <Fold id="site-theme" title="Default theme" summary={form.defaultTheme}>
        <p className="muted small">
          The default theme: what parents and students see until they pick their own in Settings or the Student Portal.
          Anyone who has picked one keeps it.
        </p>
        <ThemeGrid value={form.defaultTheme} onChange={(t) => set('defaultTheme', t)} />
        </Fold>
        <Fold id="site-agent-theme" title="Default School Resources theme" summary={paletteById(form.agentDefaultTheme).name}>
        <label>
          Default School Resources theme
          <select value={form.agentDefaultTheme} onChange={(e) => set('agentDefaultTheme', e.target.value)}>
            {AGENT_THEME_IDS.map((id) => (
              <option key={id} value={id}>
                {paletteById(id).name}
              </option>
            ))}
          </select>
          <span className="muted small">
            How School Resources look on a child’s computer until the child picks one of these themes (in the app or the Student Portal). A child’s own
            pick, when it’s one of these, is kept.
          </span>
        </label>
        </Fold>
        {/* Hidden mode (#829). */}
        <Fold
          id="site-agent-hiding"
          title="Hiding School Resources"
          summary={form.agentHiding.allowed ? `Parents can hide it · ${form.agentHiding.byDefault ? 'hidden' : 'shown'} for new children` : 'Always shown'}
        >
        <label className="check">
          <input type="checkbox" checked={form.agentHiding.allowed} onChange={(e) => set('agentHiding', { ...form.agentHiding, allowed: e.target.checked })} />
          <span>
            <strong>Allow parents to hide the agent</strong>
            <span className="muted small block">
              Shows “Hide School Resources” on each child’s page. Off: the switch isn’t offered and every agent stays visible.
            </span>
          </span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={form.agentHiding.allowed && form.agentHiding.byDefault}
            disabled={!form.agentHiding.allowed}
            onChange={(e) => set('agentHiding', { ...form.agentHiding, byDefault: e.target.checked })}
          />
          <span>
            <strong>Hidden by default for new children</strong>
            <span className="muted small block">Parents can still change it per child.</span>
          </span>
        </label>
        </Fold>
        <Fold id="site-css" title="Custom styling (CSS)" summary={cssSummary}>
        <p className="muted small">
          Change how ClassGuardian looks with your own CSS. “Every page” loads first, so it’s the place to change the default themes, for example their
          colors: <code>:root {'{'} --primary: #0a7d5a; {'}'}</code>. The others apply on top of it, to one part of the app. Images and fonts must come
          from this server. Leave a box empty to use the standard look.
        </p>
        {CSS_AREAS.map((a) => (
          <label key={a.id}>
            {a.label}
            <textarea
              className="mono"
              rows={4}
              maxLength={50000}
              spellCheck={false}
              value={form.customCss[a.id]}
              onChange={(e) => set('customCss', { ...form.customCss, [a.id]: e.target.value })}
              aria-label={`Custom CSS: ${a.label}`}
            />
          </label>
        ))}
        <CssViewer
          areas={CSS_AREAS}
          onAdd={(area, css) => {
            const cur = form.customCss[area as keyof typeof form.customCss] ?? '';
            set('customCss', { ...form.customCss, [area]: cur.trim() ? `${cur.trimEnd()}\n\n${css}` : css });
          }}
        />
        </Fold>
      </Collapsible>

      <h3 className="settings-group">Families and accounts</h3>
      <Collapsible id="s-accounts" title="Sign-up and accounts" anchors={['accounts-admins']}>
        <label className="check">
          <input type="checkbox" checked={form.registrationOpen} onChange={(e) => set('registrationOpen', e.target.checked)} />
          <span>
            Allow new parents to sign up
            <span className="muted small block">Turn off to make this a private server: only existing accounts can sign in.</span>
          </span>
        </label>
        <Fold
          title="Get started checklist"
          summary={form.onboarding.enabled ? `On · ${form.onboarding.steps.length} step${form.onboarding.steps.length === 1 ? '' : 's'}` : 'Off'}
        >
        <label className="check">
          <input type="checkbox" checked={form.onboarding.enabled} onChange={(e) => set('onboarding', { ...form.onboarding, enabled: e.target.checked })} />
          <span>
            Show new families a <strong>Get started</strong> checklist on Home
            <span className="muted small block">It ticks itself off as they go, and parents can hide it. These steps are on it:</span>
          </span>
        </label>
        <div className="chips onboarding-steps" role="group" aria-label="Checklist steps">
          {(
            [
              ['child', 'Add a child'],
              ['pair', 'Set up their computer'],
              ['hours', 'Set school hours'],
              ['assignment', 'Add a first assignment'],
            ] as const
          ).map(([id, label]) => {
            const on = form.onboarding.steps.includes(id);
            return (
              <button
                key={id}
                type="button"
                className={`chip ${on ? 'active' : ''}`}
                aria-pressed={on}
                disabled={!form.onboarding.enabled}
                onClick={() => set('onboarding', { ...form.onboarding, steps: on ? form.onboarding.steps.filter((x) => x !== id) : ['child', 'pair', 'hours', 'assignment'].filter((x) => x === id || form.onboarding.steps.includes(x)) })}
              >
                {label}
              </button>
            );
          })}
        </div>
        </Fold>
        <Fold id="accounts-admins" title="Admins" summary={`${adminCount} admin${adminCount === 1 ? '' : 's'}`}>
        {saved.envAdminEmails.length > 0 && (
          <div className="chips">
            {saved.envAdminEmails.map((e) => (
              <span key={e} className="chip static" title="Set by ADMIN_EMAILS on the server; change it there">
                <Lock size={13} /> {e}
              </span>
            ))}
          </div>
        )}
        <label>
          More admins <span className="muted small">(one email per line; they must sign up as parents first)</span>
          <textarea rows={2} value={form.adminEmails} onChange={(e) => set('adminEmails', e.target.value)} />
        </label>
        </Fold>
      </Collapsible>

      <Collapsible id="s-security" title="Security">
        <label>
          Require two-factor sign-in
          <select value={form.requireMfa} onChange={(e) => set('requireMfa', e.target.value as SettingsForm['requireMfa'])}>
            <option value="off">Optional (each parent chooses)</option>
            <option value="admins">Admins must use it</option>
            <option value="everyone">Everyone must use it</option>
          </select>
          <span className="muted small">When required, affected accounts must set it up before they can use the dashboard.</span>
        </label>
        <Fold title="Passkeys" summary={form.passkeys.enabled ? (form.passkeys.countAsMfa ? 'On · counts as two-factor' : 'On') : 'Off'}>
          <PasskeyPolicySettings value={form.passkeys} onChange={(v) => set('passkeys', v)} publicUrl={form.publicUrl} />
        </Fold>
        <Fold
          title="Failed sign-ins"
          summary={
            form.lockout.enabled
              ? `On · cooldown after ${form.lockout.cooldownAfter}${form.lockout.lockAfter ? `, lock after ${Math.max(form.lockout.lockAfter, form.lockout.cooldownAfter + 1)}` : ', never locks'}`
              : 'Off'
          }
        >
          <LockoutSettings value={form.lockout} onChange={(v) => set('lockout', v)} />
        </Fold>
        <Fold title="Sign-in report" summary="Failed sign-ins and lockouts">
          <SigninSecurityReport />
        </Fold>
        <label className="check">
          <input type="checkbox" checked={form.webhookAllowPrivate} onChange={(e) => set('webhookAllowPrivate', e.target.checked)} />
          <span>
            Allow webhooks to reach private/LAN addresses
            <span className="muted small block">
              Off by default. Turn on only if parents need webhooks to hit devices on your local network (e.g. Home Assistant).
              Cloud-metadata and loopback addresses are always blocked.
            </span>
          </span>
        </label>
      </Collapsible>

      {/* API access (#658): was its own Admin tab. Families start with it off; the list scrolls as it grows. */}
      <Collapsible id="s-api" title="API access">
        <div className="settings-scroll">
          <ApiAccess />
        </div>
      </Collapsible>

      <Collapsible id="s-features" title="Features" anchors={['features-new-children']}>
        <p className="muted small">
          Turn a feature off for everyone on this server, for example while it’s being fixed. It overrides every plan and every family’s own
          choices: it disappears from parents’ pages, the Student Portal and School Resources, and the server refuses it. Turn it back on and
          everything returns; nothing is deleted.
        </p>
        {[...CHILD_FEATURE_GROUPS, { label: 'For the family', items: FAMILY_FEATURES }].map((g) => (
          <Fold key={g.label} title={g.label} summary={`${g.items.filter((f) => !form.disabledFeatures.includes(f.key)).length} of ${g.items.length} on`}>
            <div className="feature-checks" role="group" aria-label={g.label}>
              {g.items.map((f) => (
                <label key={f.key} className="check">
                  <input
                    type="checkbox"
                    checked={!form.disabledFeatures.includes(f.key)}
                    onChange={(e) => set('disabledFeatures', e.target.checked ? form.disabledFeatures.filter((k) => k !== f.key) : [...form.disabledFeatures, f.key])}
                  />
                  <span>
                    {f.label}
                    {form.disabledFeatures.includes(f.key) && <span className="tag bug">Off for everyone</span>}
                    <span className="muted small block">{f.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </Fold>
        ))}
        <Fold
          id="features-new-children"
          title="Features new children start hidden"
          summary={form.defaultHiddenFeatures.length ? `${form.defaultHiddenFeatures.length} hidden` : 'None'}
        >
          <p className="muted small">Parents can show or hide any feature for each child in Child → Student → Features. Pick what a new child starts without.</p>
          <div className="feature-checks" role="group" aria-label="Features new children start hidden">
            {CHILD_FEATURE_GROUPS.flatMap((g) => g.items).map((f) => (
              <label key={f.key} className="check">
                <input
                  type="checkbox"
                  checked={form.defaultHiddenFeatures.includes(f.key)}
                  onChange={(e) => set('defaultHiddenFeatures', e.target.checked ? [...form.defaultHiddenFeatures, f.key] : form.defaultHiddenFeatures.filter((k) => k !== f.key))}
                />
                <span>{f.label}</span>
              </label>
            ))}
          </div>
        </Fold>
        <label className="check">
          <input type="checkbox" checked={form.alsoForAll} onChange={(e) => set('alsoForAll', e.target.checked)} />
          <span>
            Add sites and apps for all of a family’s children by default
            <span className="muted small block">
              When a parent blocks or approves a site or app for one child, “Also for” starts with their other children ticked, so it’s added for
              them too; they untick the exception. Off: it starts with just that child.
            </span>
          </span>
        </label>
      </Collapsible>

      <Collapsible id="s-retention" title="Data retention">
        <p className="muted small">How long the server keeps records before removing them automatically.</p>
        <label>
          Keep activity history for (days)
          <input type="number" min={1} max={3650} value={form.retentionDays} onChange={(e) => set('retentionDays', Number(e.target.value))} />
        </label>
        <label>
          Keep sign-in history for (days)
          <input type="number" min={1} max={3650} value={form.signInHistoryDays} onChange={(e) => set('signInHistoryDays', Number(e.target.value))} />
          <span className="muted small block">For Audit log → Sign-ins. Each parent’s sign-in total is kept for good.</span>
        </label>
        <label>
          Days before a requested account deletion
          <input type="number" min={0} max={90} value={form.accountDeletionDays} onChange={(e) => set('accountDeletionDays', Number(e.target.value))} />
          <span className="muted small block">When a parent deletes their account, it’s erased after this many days; signing in before then keeps it. 0 erases it at once.</span>
        </label>
          <label>
            Keep work samples for (years)
            <input type="number" min={0} max={10} value={form.portfolio.retentionYears} onChange={(e) => set('portfolio', { ...form.portfolio, retentionYears: Number(e.target.value) })} />
          <span className="muted small block">
            0 keeps them until someone deletes them. With a number set, older samples are removed automatically, and families get an email a month before so
            they can download their portfolio first.
          </span>
        </label>
      </Collapsible>

      <h3 className="settings-group">The child’s computer</h3>
      <Collapsible id="s-agent" title="Agent (Windows and Mac)">
        <label>
          Shrink the School Resources button after (seconds)
          <input type="number" min={0} max={300} value={form.launcherCollapseSeconds} onChange={(e) => set('launcherCollapseSeconds', Number(e.target.value))} />
          <span className="muted small block">
            On the child’s computer, the floating School Resources button folds down to just the ClassGuardian logo, with its status dot underneath,
            this long after the pointer leaves it; pointing at it opens it again. 0 keeps it full size. 5 by default.
          </span>
        </label>
        <label>
          Check for updates every (hours)
          <input type="number" min={1} max={168} value={form.agentUpdateHours} onChange={(e) => set('agentUpdateHours', Number(e.target.value))} />
          <span className="muted small block">
            How often the Windows and Mac apps on children’s computers check in for a new version and install it. 1 to 168 (a week); 6 by default. A computer picks up a new
            value at its next check. <strong>Check for updates</strong> in the app and updates you push from a child’s Devices tab still run right away.
          </span>
        </label>
        <label>
          Alert a parent when the app goes offline during school time after (seconds)
          <input type="number" min={5} max={3600} value={form.offlineGraceSeconds} onChange={(e) => set('offlineGraceSeconds', Number(e.target.value))} />
        </label>
        {/* Only what isn't allowed alerts a parent (#847). */}
        <label className="check">
          <input type="checkbox" checked={form.offTaskAlerts} onChange={(e) => set('offTaskAlerts', e.target.checked)} />
          <span>
            <strong>Alert when a child leaves the learning sites</strong>
            <span className="muted small block">
              Off: parents are alerted only when a child tries a blocked site or app. On: also whenever they move to an allowed site that isn’t
              a learning site, which happens many times a day.
            </span>
          </span>
        </label>
        {/* One-click Student Portal from the app (#684). */}
        <label className="check">
          <input type="checkbox" checked={form.studentPortalLinks} onChange={(e) => set('studentPortalLinks', e.target.checked)} />
          <span>
            <strong>One-click Student Portal sign-in</strong>
            <span className="muted small block">
              <strong>Student Portal…</strong> in the app’s menu and <strong>Open my Student Portal</strong> in School Resources open the portal in the
              computer’s browser, already signed in as whoever is using it, with a link that works once for two minutes. Off: they’re hidden, and
              children sign in with their code and password.
            </span>
          </span>
        </label>
      </Collapsible>

      <Collapsible id="s-browser-extension-auto-i" title="Browser extension">
        <p className="muted small">
          Publish the ClassGuardian extension as <strong>unlisted</strong> on the Chrome Web Store and Edge Add-ons, then paste
          its id here. The app will install it automatically in each browser on the child’s PC, pair it, and keep it updated.
          Leave blank to have parents add it by hand instead.
        </p>
        {!form.extChromeId && !form.extEdgeId && (
          <p className="setting-warn">
            ⚠️ No store ids set, so the extension <strong>won’t auto-install on home PCs.</strong> Chrome and Edge only
            force-install extensions that come from their own stores on computers that aren’t joined to a company domain —
            which home PCs never are — so the self-hosted copy is ignored. Publish the extension to both stores (unlisted) and
            paste the ids here. See <strong>docs/extension-store-publishing.md</strong> for a step-by-step guide.
          </p>
        )}
        <label>
          Chrome extension id <span className="muted small">(from the Web Store listing; 32 letters)</span>
          <input value={form.extChromeId} onChange={(e) => set('extChromeId', e.target.value.trim())} placeholder="abcdefghijklmnopabcdefghijklmnop" autoComplete="off" spellCheck={false} />
        </label>
        <label>
          Edge extension id <span className="muted small">(from the Edge Add-ons listing)</span>
          <input value={form.extEdgeId} onChange={(e) => set('extEdgeId', e.target.value.trim())} placeholder="(optional)" autoComplete="off" spellCheck={false} />
        </label>
        <p className="muted small">After pasting, it reaches each child’s PC within a few hours, or right away the next time they pair the app.</p>
        <ExtensionPackage />
        {/* School site readers (#654). */}
        <label className="check">
          <input type="checkbox" checked={form.schoolReaders} onChange={(e) => set('schoolReaders', e.target.checked)} />
          <span>
            <strong>School site readers</strong>
            <span className="muted small block">
              The extension on the child’s computer reads their planner and grades from supported school sites (Connexus) with the child’s own
              sign-in, for children whose parent turns it on. Off: families don’t see the option, and nothing is read.
            </span>
          </span>
        </label>
        <label>
          Read in the background every (hours)
          <input type="number" min={0} max={24} value={form.schoolReadHours} onChange={(e) => set('schoolReadHours', Number(e.target.value))} />
          <span className="muted small">While the child’s browser is open. 0 = only when they open the school site or a parent presses Update now.</span>
        </label>
        {/* Connexus attendance from a parent's own browser (#680). */}
        <label className="check">
          <input type="checkbox" checked={form.connexusBookmark} onChange={(e) => set('connexusBookmark', e.target.checked)} />
          <span>
            <strong>Connexus attendance bookmark</strong>
            <span className="muted small block">
              Parents can send Connexus’s own attendance (Learning Coach) from their browser with a “Send to ClassGuardian” bookmark. No password is
              stored. Off: families don’t see it, and bookmarks already made stop working.
            </span>
          </span>
        </label>
      </Collapsible>

      <Collapsible id="s-live-view" title="Live view and cameras" anchors={['live-turn', 'live-blur']}>
        <Fold id="live-turn" title="STUN and TURN servers" summary={`${stunCount} STUN · ${lines(form.turnUrls).length ? 'TURN set' : 'no TURN'}`}>
        <p className="muted small">
          STUN finds a direct route between parent and child. TURN relays the video when networks block direct connections.
        </p>
        <label>
          STUN server URLs <span className="muted small">(one per line)</span>
          <textarea rows={2} value={form.stunUrls} onChange={(e) => set('stunUrls', e.target.value)} />
        </label>
        <label>
          TURN server URLs <span className="muted small">(one per line, e.g. turn:turn.example.com:3478)</span>
          <textarea rows={2} value={form.turnUrls} onChange={(e) => set('turnUrls', e.target.value)} />
        </label>
        <label>
          TURN username
          <input value={form.turnUser} onChange={(e) => set('turnUser', e.target.value)} autoComplete="off" />
        </label>
        <SecretField
          label="TURN password"
          isSet={saved.turn.credentialSet}
          value={form.turnCred}
          cleared={form.turnCredClear}
          onChange={(v) => set('turnCred', v)}
          onClear={(c) => set('turnCredClear', c)}
        />
        </Fold>
        {/* Camera blur (#592). */}
        <Fold id="live-blur" title="Camera blur" summary={blurSummary}>
        <label className="check">
          <input type="checkbox" checked={form.cameraBlur.parent} onChange={(e) => set('cameraBlur', { ...form.cameraBlur, parent: e.target.checked })} />
          <span>
            <strong>Offer “Blur background” on the parent’s camera</strong>
            <span className="muted small block">Parents can blur their room when they turn on their camera in live view. It runs in the parent’s browser, not on the server.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.cameraBlur.child} onChange={(e) => set('cameraBlur', { ...form.cameraBlur, child: e.target.checked })} />
          <span>
            <strong>Offer “Blur my camera background” to children</strong>
            <span className="muted small block">Children can blur their room from the ClassGuardian menu on Windows and Mac, when their parent allows it. Done on the child’s computer.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.cameraBlur.byDefault} onChange={(e) => set('cameraBlur', { ...form.cameraBlur, byDefault: e.target.checked })} />
          <span>
            <strong>Blur by default</strong>
            <span className="muted small block">Turn blur on for parents and children who haven’t chosen yet. Each person’s own choice is remembered.</span>
          </span>
        </label>
        </Fold>
        <Fold id="live-ghost" title="Ghost mode" summary={form.ghostMode.allowed ? 'Offered to parents' : 'Off'}>
        <label className="check">
          <input type="checkbox" checked={form.ghostMode.allowed} onChange={(e) => set('ghostMode', { allowed: e.target.checked })} />
          <span>
            <strong>Let parents watch a child’s screen without the banner</strong>
            <span className="muted small block">Each child has a “Ghost mode” switch on their Features tab, off until the parent turns it on. It hides the “a parent is watching” banner for that child’s screen only — the camera and microphone always show it. Turn this off to switch it off for every family.</span>
          </span>
        </label>
        </Fold>
      </Collapsible>

      <h3 className="settings-group">Homeschool records</h3>
      {/* Work samples and the portfolio (#603). */}
      <Collapsible id="s-portfolio" title="Work samples and portfolio">
        <label className="check">
          <input type="checkbox" checked={form.portfolio.enabled} onChange={(e) => set('portfolio', { ...form.portfolio, enabled: e.target.checked })} />
          <span>
            <strong>Work samples and the portfolio PDF</strong>
            <span className="muted small block">Parents add photos and PDFs of their child’s work to assignments and journal entries, and make a year-end portfolio PDF. Part of the Portfolio plan feature.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.portfolio.studentUploads} onChange={(e) => set('portfolio', { ...form.portfolio, studentUploads: e.target.checked })} />
          <span>
            <strong>Children can add their work when they turn it in</strong>
            <span className="muted small block">In the Student Portal and in School Resources on Windows and Mac. Parents decide what goes in the portfolio.</span>
          </span>
        </label>
        <div className="field-row wrap-row">
          <label>
            Largest file (MB)
            <input type="number" min={1} max={25} value={form.portfolio.maxMb} onChange={(e) => set('portfolio', { ...form.portfolio, maxMb: Number(e.target.value) })} />
          </label>
          <label>
            Space per family (MB)
            <input type="number" min={50} max={5000} value={form.portfolio.familyStorageMb} onChange={(e) => set('portfolio', { ...form.portfolio, familyStorageMb: Number(e.target.value) })} />
          </label>
        </div>
        <label>
          Biggest document a family can upload (MB)
          <input type="number" min={1} max={100} value={form.portfolio.documentMaxMb} disabled={!form.portfolio.documents} onChange={(e) => set('portfolio', { ...form.portfolio, documentMaxMb: Number(e.target.value) })} />
        </label>
        <span className="muted small">
          Work samples, test reports and Homeschool documents share each family’s space. Files are kept in the database, so backups include them; photos are
          shrunk to 2,000 pixels before upload. How long samples are kept is under Data retention.
        </span>
      </Collapsible>

      <Collapsible id="s-records" title="Records">
        <label className="check">
          <input type="checkbox" checked={form.portfolio.tests} onChange={(e) => set('portfolio', { ...form.portfolio, tests: e.target.checked })} />
          <span>
            <strong>Tests and evaluations</strong>
            <span className="muted small block">Families record standardized tests and evaluations, with the scanned report, on the Portfolio tab.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.portfolio.fieldTrips} onChange={(e) => set('portfolio', { ...form.portfolio, fieldTrips: e.target.checked })} />
          <span>
            <strong>Field trips</strong>
            <span className="muted small block">Families record field trips on the Portfolio tab, with photos as work samples. A trip can count as school time on that day.</span>
          </span>
        </label>
        <div className="field-row wrap-row">
          <label>
            Photos per trip
            <input type="number" min={1} max={20} value={form.portfolio.tripPhotos} disabled={!form.portfolio.fieldTrips} onChange={(e) => set('portfolio', { ...form.portfolio, tripPhotos: Number(e.target.value) })} />
          </label>
          <label>
            Most hours a trip can count
            <input type="number" min={1} max={12} step={0.5} value={form.portfolio.tripMaxHours} disabled={!form.portfolio.fieldTrips} onChange={(e) => set('portfolio', { ...form.portfolio, tripMaxHours: Number(e.target.value) })} />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.portfolio.transcript} onChange={(e) => set('portfolio', { ...form.portfolio, transcript: e.target.checked })} />
          <span>
            <strong>High school transcript</strong>
            <span className="muted small block">Families keep courses, credits and GPA for 9th to 12th grade and make a transcript PDF.</span>
          </span>
        </label>
        <div className="field-row wrap-row">
          <label>
            Honors GPA bonus
            <input type="number" min={0} max={2} step={0.25} value={form.portfolio.honorsBonus} disabled={!form.portfolio.transcript} onChange={(e) => set('portfolio', { ...form.portfolio, honorsBonus: Number(e.target.value) })} />
          </label>
          <label>
            AP / dual enrollment bonus
            <input type="number" min={0} max={2} step={0.25} value={form.portfolio.apBonus} disabled={!form.portfolio.transcript} onChange={(e) => set('portfolio', { ...form.portfolio, apBonus: Number(e.target.value) })} />
          </label>
          <label>
            Credits for a new course
            <input type="number" min={0.25} max={4} step={0.25} value={form.portfolio.defaultCredits} disabled={!form.portfolio.transcript} onChange={(e) => set('portfolio', { ...form.portfolio, defaultCredits: Number(e.target.value) })} />
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.portfolio.documents} onChange={(e) => set('portfolio', { ...form.portfolio, documents: e.target.checked })} />
          <span>
            <strong>Homeschool documents</strong>
            <span className="muted small block">Families keep filed paperwork (notices, acceptance letters, reports, diplomas) in Settings → School Settings. Files share the family’s space above.</span>
          </span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.portfolio.closeOut} onChange={(e) => set('portfolio', { ...form.portfolio, closeOut: e.target.checked })} />
          <span>
            <strong>Close out the school year</strong>
            <span className="muted small block">Families close a finished year in School Settings: each child’s portfolio goes to Homeschool documents and the year’s records are locked until they reopen it.</span>
          </span>
        </label>
      </Collapsible>

      <Collapsible id="s-evaluator-links" title="Evaluator links">
        <label className="check">
          <input type="checkbox" checked={form.portfolio.evaluatorLinks} onChange={(e) => set('portfolio', { ...form.portfolio, evaluatorLinks: e.target.checked })} />
          <span>
            <strong>Evaluator links</strong>
            <span className="muted small block">Families share a read-only link to a child’s school year with an evaluator, no account needed. Off: every link stops working.</span>
          </span>
        </label>
        <div className="field-row wrap-row">
          <label>
            A link lasts at most (days)
            <input type="number" min={1} max={365} value={form.portfolio.linkMaxDays} disabled={!form.portfolio.evaluatorLinks} onChange={(e) => set('portfolio', { ...form.portfolio, linkMaxDays: Number(e.target.value) })} />
          </label>
          <label>
            Working links per family
            <input type="number" min={1} max={100} value={form.portfolio.linksPerFamily} disabled={!form.portfolio.evaluatorLinks} onChange={(e) => set('portfolio', { ...form.portfolio, linksPerFamily: Number(e.target.value) })} />
          </label>
        </div>
      </Collapsible>

      <h3 className="settings-group">Email and messages</h3>
      <Collapsible id="s-email-smtp" title="Email (SMTP)">
        <p className="muted small">
          Used for focus alerts to parents who turn them on, and to tell admins when feedback arrives.{' '}
          <strong className={saved.smtp.configured ? 'good-text' : ''}>{saved.smtp.configured ? 'Email is set up.' : 'Not set up yet.'}</strong>
        </p>
        <Fold
          title="SMTP server"
          summary={form.smtpHost.trim() ? `${form.smtpHost.trim()} · ${form.smtpSecurity === 'tls' ? 'TLS' : form.smtpSecurity === 'starttls' ? 'STARTTLS' : 'no encryption'}` : 'Not set up'}
        >
        <div className="form-row">
          <label className="grow">
            SMTP server
            <input value={form.smtpHost} onChange={(e) => set('smtpHost', e.target.value)} placeholder="smtp.example.com" autoComplete="off" />
          </label>
          <label className="w-port">
            Port
            <input type="number" min={1} max={65535} value={form.smtpPort} onChange={(e) => set('smtpPort', Number(e.target.value))} />
          </label>
        </div>
        <label>
          Security
          <select
            value={form.smtpSecurity}
            onChange={(e) => {
              const v = e.target.value as SettingsForm['smtpSecurity'];
              set('smtpSecurity', v);
              set('smtpPort', v === 'tls' ? 465 : v === 'starttls' ? 587 : 25);
            }}
          >
            <option value="starttls">STARTTLS (usually port 587)</option>
            <option value="tls">TLS (usually port 465)</option>
            <option value="none">None (only on a trusted network)</option>
          </select>
        </label>
        <label>
          Username
          <input value={form.smtpUser} onChange={(e) => set('smtpUser', e.target.value)} autoComplete="off" />
        </label>
        <SecretField
          label="Password"
          isSet={saved.smtp.passwordSet}
          value={form.smtpPass}
          cleared={form.smtpPassClear}
          onChange={(v) => set('smtpPass', v)}
          onClear={(c) => set('smtpPassClear', c)}
        />
        <label>
          Send as <span className="muted small">(e.g. ClassGuardian &lt;alerts@example.com&gt;)</span>
          <input value={form.smtpFrom} onChange={(e) => set('smtpFrom', e.target.value)} autoComplete="off" />
        </label>
        <div>
          <button className="btn small" onClick={testEmail} disabled={!saved.smtp.configured || dirty}>
            Send a test email to {parent.email}
          </button>
          {dirty && <span className="muted small"> Save first.</span>}
        </div>
        </Fold>
        <Fold title="Email usage" summary="Emails sent, by day and month">
          <EmailUsage />
        </Fold>
      </Collapsible>

      <Collapsible id="s-weekly-summary" title="Weekly summary email">
        <p className="muted small">
          Once a week, each parent gets one email about each child they can see: school time, attendance, assignments, grades and reading, and what needs attention.
          It goes out at the day and hour below in each parent’s own time zone, to families whose plan has Activity reports. Parents can turn it off in Settings →
          Notifications.
        </p>
        <label className="check">
          <input type="checkbox" checked={form.weeklySummary.enabled} onChange={(e) => set('weeklySummary', { ...form.weeklySummary, enabled: e.target.checked })} />
          <span>Send the weekly summary</span>
        </label>
        <Fold title="When and who" summary={weeklySummary}>
        <div className="row-gap wrap">
          <label>
            Day
            <select value={form.weeklySummary.day} disabled={!form.weeklySummary.enabled} onChange={(e) => set('weeklySummary', { ...form.weeklySummary, day: Number(e.target.value) })}>
              {WEEKDAYS.map((d, i) => (
                <option key={d} value={i}>
                  {d}
                </option>
              ))}
            </select>
          </label>
          <label>
            Time
            <select value={form.weeklySummary.hour} disabled={!form.weeklySummary.enabled} onChange={(e) => set('weeklySummary', { ...form.weeklySummary, hour: Number(e.target.value) })}>
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {hourLabel(h)}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={form.weeklySummary.defaultOn} disabled={!form.weeklySummary.enabled} onChange={(e) => set('weeklySummary', { ...form.weeklySummary, defaultOn: e.target.checked })} />
          <span>
            On for parents who haven’t chosen
            <span className="muted small block">Off: only parents who turn it on in Settings get it.</span>
          </span>
        </label>
        <div>
          <button type="button" className="btn" onClick={testWeekly} disabled={!saved.smtp.configured}>
            Send me mine now
          </button>
        </div>
        </Fold>
      </Collapsible>

      <Collapsible id="s-admin-notifications" title="Admin notifications">
        <p className="muted small">Email the admins about platform events (needs SMTP set up).</p>
        <label className="check">
          <input type="checkbox" checked={form.notifySignups} onChange={(e) => set('notifySignups', e.target.checked)} />
          <span>A new family signs up</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.notifyFeedback} onChange={(e) => set('notifyFeedback', e.target.checked)} />
          <span>A Help &amp; Feedback request arrives</span>
        </label>
      </Collapsible>

      <h3 className="settings-group">Business</h3>
      <Collapsible id="s-payments" title="Payments (Stripe)">
        <PaymentsSettings saved={saved} onSaved={apply} />
      </Collapsible>
      {/* Its own save (#871): the trial, renewal, receipt, failed-payment and cancellation emails, by billing state. */}
      {/* Discount and Founder codes for sign-up (#879). */}
      <Collapsible id="s-signup-codes" title="Sign-up codes">
        <SignupCodes founderPercent={saved.billing?.foundersPercent ?? 50} />
      </Collapsible>
      <Collapsible id="s-billing-notices" title="Billing notices">
        <BillingNotices />
      </Collapsible>

      <Collapsible id="s-support" title="Help &amp; Feedback" anchors={['support-github']}>
        <SupportSettings value={form.support} onChange={(v) => set('support', v)} notifyNew={form.notifyFeedback} onNotifyNew={(v) => set('notifyFeedback', v)} />
        <h3 id="support-github" className="settings-sub">GitHub (feedback → issues)</h3>
        <p className="muted small">
          Approved feedback is filed as an issue here. Best: a <strong>GitHub App</strong> installed on this repository with
          <strong> Issues: Read and write</strong>, so issues are filed by the app’s bot rather than a person. A personal access
          token still works, but issues then show as filed by that person.{' '}
          <strong className={saved.github.configured ? 'good-text' : ''}>
            {saved.github.mode === 'app' ? 'Connected as the GitHub App.' : saved.github.mode === 'token' ? 'Connected with a personal token.' : 'Not connected yet.'}
          </strong>
        </p>
        <label>
          Repository <span className="muted small">(owner/name)</span>
          <input value={form.ghRepo} onChange={(e) => set('ghRepo', e.target.value)} placeholder="owner/repository" autoComplete="off" />
        </label>
        <Fold
          title="GitHub App"
          summary={saved.github.mode === 'app' ? 'Connected' : form.ghAppId.trim() ? `App ID ${form.ghAppId.trim()}` : 'Not set up'}
        >
        <label>
          App ID <span className="muted small">(on the app’s settings page)</span>
          <input value={form.ghAppId} onChange={(e) => set('ghAppId', e.target.value)} inputMode="numeric" placeholder="e.g. 1234567" autoComplete="off" />
        </label>
        <label>
          Private key <span className="muted small">(the .pem file’s contents)</span>
          <textarea
            rows={4}
            value={form.ghKey}
            onChange={(e) => set('ghKey', e.target.value)}
            placeholder={saved.github.privateKeySet && !form.ghKeyClear ? 'Saved. Leave blank to keep it.' : '-----BEGIN RSA PRIVATE KEY-----'}
            disabled={form.ghKeyClear}
            spellCheck={false}
            autoComplete="off"
            className="mono"
          />
          {saved.github.privateKeySet && (
            <span className="muted small">
              {form.ghKeyClear ? 'Will be removed when you save. ' : ''}
              <button type="button" className="link-btn" onClick={() => set('ghKeyClear', !form.ghKeyClear)}>
                {form.ghKeyClear ? 'Keep it' : 'Remove saved key'}
              </button>
            </span>
          )}
        </label>
        </Fold>
        <Fold
          title="Or a personal access token"
          summary={saved.github.tokenSet && !form.ghTokenClear ? (saved.github.mode === 'token' ? 'Connected' : 'Saved') : form.ghToken.trim() ? 'Not saved yet' : 'Not set up'}
        >
        <SecretField
          label="Access token"
          isSet={saved.github.tokenSet}
          value={form.ghToken}
          cleared={form.ghTokenClear}
          onChange={(v) => set('ghToken', v)}
          onClear={(c) => set('ghTokenClear', c)}
        />
        </Fold>
        <div>
          <button className="btn small" onClick={testGithub} disabled={!saved.github.configured || dirty}>
            Test connection
          </button>
          {dirty && <span className="muted small"> Save first.</span>}
        </div>
      </Collapsible>

      <Collapsible id="s-terms" title="Terms and privacy">
        <TermsSettings saved={saved} onSaved={apply} />
      </Collapsible>

      <h3 className="settings-group">Server</h3>
      <Collapsible id="s-server" anchors={['server-container']} title={<>Server <span className="tag restart">Applies after a restart</span></>}>
        <label>
          Listen address <span className="muted small">(0.0.0.0 = every network interface)</span>
          <input value={form.host} onChange={(e) => set('host', e.target.value)} />
        </label>
        <label>
          Port
          <input type="number" min={1} max={65535} value={form.port} onChange={(e) => set('port', Number(e.target.value))} />
          <span className="muted small">In Docker this is the port inside the container. Change the container’s port mapping to match.</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={form.trustProxy} onChange={(e) => set('trustProxy', e.target.checked)} />
          <span>
            Behind an HTTPS reverse proxy
            <span className="muted small block">Turn on when Caddy, nginx or a load balancer sits in front of ClassGuardian.</span>
          </span>
        </label>
        <Fold id="server-container" title="Set by the container" summary={saved.encryptedAtRest ? 'Secrets encrypted' : 'Secrets not encrypted'}>
        <p className="muted small">These can only change in the Docker container’s settings, because the database lives in the data folder.</p>
        <dl className="admin-env">
          <dt>Data folder</dt>
          <dd className="mono">{saved.fixed.dataDir}</dd>
          <dt>File owner (PUID:PGID)</dt>
          <dd className="mono">{saved.fixed.puid ? `${saved.fixed.puid}:${saved.fixed.pgid}` : '—'}</dd>
          <dt>Secret encryption at rest</dt>
          <dd>
            {saved.encryptedAtRest ? (
              <span className="good-text">On — TOTP seeds and SMTP/TURN/GitHub credentials are encrypted in the database.</span>
            ) : (
              <span className="muted">
                Off. Set <code className="mono">ENCRYPTION_KEY</code> in the container to encrypt stored secrets, and encrypt the data
                volume for defence in depth.
              </span>
            )}
          </dd>
        </dl>
        </Fold>
      </Collapsible>
        </>
      )}

      {/* Only when something changed (#590); saving or discarding hides it again. */}
      {(dirty || busy) && (
        <div className="save-bar">
          <span className="muted small grow">You have unsaved changes.</span>
          <button className="btn" onClick={() => setForm(toForm(saved))} disabled={busy}>
            Discard
          </button>
          <button className="btn primary" onClick={save} disabled={busy || !dirty}>
            {busy ? 'Saving…' : 'Save server settings'}
          </button>
        </div>
      )}
    </div>
  );
}
function SecretField(props: {
  label: string;
  isSet: boolean;
  value: string;
  cleared: boolean;
  onChange: (v: string) => void;
  onClear: (cleared: boolean) => void;
}) {
  const { label, isSet, value, cleared, onChange, onClear } = props;
  return (
    <label>
      {label}
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isSet && !cleared ? 'Saved. Leave blank to keep it.' : ''}
        disabled={cleared}
        autoComplete="new-password"
      />
      {isSet && (
        <span className="muted small">
          {cleared ? 'Will be removed when you save. ' : ''}
          <button type="button" className="link-btn" onClick={() => onClear(!cleared)}>
            {cleared ? 'Keep it' : 'Remove saved value'}
          </button>
        </span>
      )}
    </label>
  );
}
function RestartNotice({ saved }: { saved: PlatformSettings }) {
  const { toast } = useParent();
  const [restarting, setRestarting] = useState(false);
  const changes = (Object.keys(saved.boot) as (keyof BootSettings)[])
    .filter((k) => saved.boot[k] !== saved.running[k])
    .map((k) => `${k === 'trustProxy' ? 'reverse proxy' : k === 'host' ? 'listen address' : 'port'}: ${String(saved.running[k])} → ${String(saved.boot[k])}`);

  async function restart() {
    const portChange = saved.boot.port !== saved.running.port;
    if (
      !confirm(
        `Restart the server now? Everyone is disconnected for a few seconds.${
          portChange ? `\n\nIt will listen on port ${saved.boot.port}; make sure your container's port mapping matches, or you won't be able to reach it.` : ''
        }`,
      )
    )
      return;
    setRestarting(true);
    try {
      await api.post('/api/admin/restart');
    } catch (e) {
      setRestarting(false);
      toast({ text: (e as Error).message, tone: 'alert' });
      return;
    }
    // Wait for it to go away and come back, then reload.
    await new Promise((r) => setTimeout(r, 1500));
    for (let i = 0; i < 60; i++) {
      try {
        if ((await fetch('/api/health', { cache: 'no-store' })).ok) return location.reload();
      } catch {
        /* still restarting */
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
    setRestarting(false);
    toast({ text: 'The server hasn’t come back here yet. If you changed the port, open it on the new port.', tone: 'alert' });
  }

  return (
    <div className="card notice stack">
      <div>
        <strong>Restart needed.</strong> Saved changes that apply after a restart: {changes.join('; ')}.
      </div>
      {saved.fixed.supervised ? (
        <div>
          <button className="btn small" onClick={restart} disabled={restarting}>
            <RotateCcw size={15} /> {restarting ? 'Restarting…' : 'Restart now'}
          </button>
        </div>
      ) : (
        <div className="muted small">
          Restart ClassGuardian yourself to apply them (on Unraid: Docker tab → ClassGuardian → Restart).
        </div>
      )}
    </div>
  );
}

// --- Audit log ------------------------------------------------------------------------------
